import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from '@/components/ui/toast';
import { useUser } from '@/store/useUser';
import { useUserPermissions } from '@/store/useUserPermissions';
import { hasAnyPolicy } from '@/utils/policies';
import {
  CONFIRM_USERS,
  DISABLE_USER,
  GET_SPONSORED_USERS,
  UPDATE_USER_SPONSOR,
} from './queries';
import {
  ConfirmUsersResponse,
  DisableUserResponse,
  GetUsersResponse,
  SponsorGroup,
  UpdateSponsorResponse,
} from './types';

export const useSponsorManagement = () => {
  const { user } = useUser();
  const { policies } = useUserPermissions();
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const normalizedUsername = `${user?.username || ''}`.trim().toLowerCase();
  const canUpdateSponsor = hasAnyPolicy(policies, ['admin:access~full', 'admin:access~full', 'admin:access~full']);

  const { data, loading, error, refetch } = useQuery<GetUsersResponse>(GET_SPONSORED_USERS, {
    fetchPolicy: 'network-only',
    onCompleted: (responseData) => {
      console.log('[SponsorManagement] Query completed', {
        userCount: responseData?.getSponsoredUsers?.length ?? 0,
        sample: responseData?.getSponsoredUsers?.slice(0, 2)
      });
    },
    onError: (queryError) => {
      console.error('[SponsorManagement] Query error:', queryError);
    }
  });

  const [confirmUsersMutation, { loading: confirmingUsers }] = useMutation<ConfirmUsersResponse>(
    CONFIRM_USERS,
    {
      onCompleted: (result) => {
        if (result.confirmUsers.success) {
          toast.success(`Confirmed ${result.confirmUsers.confirmedCount} user(s)`);
          setSelectedUserIds(new Set());
          refetch();
        }
        if (!result.confirmUsers.success) {
          toast.error(result.confirmUsers.errors?.[0] ?? 'Failed to confirm users');
        }
      },
      onError: (err) => {
        toast.error(err.message ?? 'Failed to confirm users');
      },
    },
  );

  const [disableUserMutation, { loading: disablingUser }] = useMutation<DisableUserResponse>(
    DISABLE_USER,
    {
      onCompleted: (result) => {
        if (result.disableUser.success) {
          toast.success('User disabled successfully');
          refetch();
        }
        if (!result.disableUser.success) {
          toast.error(result.disableUser.message ?? 'Failed to disable user');
        }
      },
      onError: (err) => {
        toast.error(err.message ?? 'Failed to disable user');
      },
    },
  );

  const [updateSponsorMutation, { loading: updatingSponsor }] = useMutation<UpdateSponsorResponse>(
    UPDATE_USER_SPONSOR,
    {
      onCompleted: (result) => {
        if (result.updateUserSponsor.success) {
          toast.success('Sponsor updated successfully');
          refetch();
        }
        if (!result.updateUserSponsor.success) {
          toast.error(result.updateUserSponsor.message ?? 'Failed to update sponsor');
        }
      },
      onError: (err) => {
        toast.error(err.message ?? 'Failed to update sponsor');
      },
    },
  );

  const users = useMemo(() => data?.getSponsoredUsers ?? [], [data?.getSponsoredUsers]);

  const showAllSponsorGroups = useMemo(() => {
    return users.some((entry) => {
      const sponsor = `${entry.sponsor || ''}`.trim().toLowerCase();
      if (!sponsor) {
        return true;
      }
      return sponsor !== normalizedUsername;
    });
  }, [normalizedUsername, users]);

  const sponsorGroups = useMemo<SponsorGroup[]>(() => {
    if (!showAllSponsorGroups) {
      return [{ sponsorName: 'My Sponsored Users', users }];
    }

    const groupMap = new Map<string, SponsorGroup>();

    users.forEach((user) => {
      const sponsorKey = user.sponsor ?? 'Unassigned';
      const sponsorDisplayName =
        user.sponsor_name ?? (sponsorKey === 'Unassigned' ? null : sponsorKey);

      if (!groupMap.has(sponsorKey)) {
        groupMap.set(sponsorKey, {
          sponsorName: sponsorKey,
          sponsorDisplayName,
          users: [],
        });
      }

      const group = groupMap.get(sponsorKey)!;
      if (!group.sponsorDisplayName && sponsorDisplayName) {
        group.sponsorDisplayName = sponsorDisplayName;
      }
      group.users.push(user);
    });

    const groups: SponsorGroup[] = [];
    groupMap.forEach((group) => {
      groups.push(group);
    });

    groups.sort((a, b) => {
      if (a.sponsorName === 'Unassigned') return 1;
      if (b.sponsorName === 'Unassigned') return -1;
      return a.sponsorName.localeCompare(b.sponsorName);
    });

    return groups;
  }, [showAllSponsorGroups, users]);

  const toggleUserSelection = useCallback((userId: number) => {
    setSelectedUserIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) {
        next.delete(userId);
        return next;
      }
      next.add(userId);
      return next;
    });
  }, []);

  const selectAll = useCallback((shouldSelect: boolean) => {
    if (shouldSelect) {
      const allIds = users.filter((u) => !u.disabled).map((u) => u.id);
      setSelectedUserIds(new Set(allIds));
    }
    if (!shouldSelect) {
      setSelectedUserIds(new Set());
    }
  }, [users]);

  const clearSelection = useCallback(() => {
    setSelectedUserIds(new Set());
  }, []);

  const confirmSelectedUsers = useCallback(async () => {
    if (selectedUserIds.size === 0) {
      toast.error('Please select at least one user');
      return;
    }

    await confirmUsersMutation({
      variables: { userIds: Array.from(selectedUserIds) },
    });
  }, [confirmUsersMutation, selectedUserIds]);

  const disableUser = useCallback(
    async (username: string) => {
      await disableUserMutation({
        variables: { username },
      });
    },
    [disableUserMutation],
  );

  const updateUserSponsor = useCallback(
    async (userId: number, newSponsor: string) => {
      await updateSponsorMutation({
        variables: { userId, newSponsor },
      });
    },
    [updateSponsorMutation],
  );

  return {
    loading,
    error,
    users,
    sponsorGroups,
    selectedUserIds,
    canUpdateSponsor,
    confirmingUsers,
    disablingUser,
    updatingSponsor,
    toggleUserSelection,
    selectAll,
    clearSelection,
    confirmSelectedUsers,
    disableUser,
    updateUserSponsor,
  };
};
